import { X } from "lucide-react";
import styles from "./ProductDetails.module.scss";
import { formatNumberToPrice } from "../../../../helpers/priceFormatting";
import type { SalesProduct } from "../ProductsTable/types";

interface ProductDetailsPanelProps {
  product: SalesProduct | null;
  isOpen: boolean;
  onClose: () => void;
}

export function ProductDetails({
  product,
  isOpen,
  onClose,
}: ProductDetailsPanelProps) {
  if (!isOpen || !product) return null;

  return (
    <div className={styles.detailsBackdrop} onClick={onClose}>
      <div
        className={styles.detailsPanel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Detalles del producto ${product.reference}`}
      >
        <div className={styles.detailsHeader}>
          <div className={styles.detailsTitleGroup}>
            <h3 className={styles.detailsTitle}>{product.reference}</h3>
            <span className={styles.detailsSubtitle}>ID: {product.id}</span>
          </div>
          <button
            className={styles.closeButton}
            aria-label="Cerrar detalles"
            onClick={onClose}
          >
            <X className={styles.closeIcon} />
          </button>
        </div>
        <div className={styles.detailsContent}>
          {product.imageUrl && (
            <div className={styles.imagePreview}>
              <img src={product.imageUrl} alt={product.reference} />
            </div>
          )}
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Línea</span>
            <span className={styles.detailsValue}>{product.line}</span>
          </div>
          {product.brand && (
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Marca</span>
              <span className={styles.detailsValue}>{product.brand}</span>
            </div>
          )}

          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Disponibles</span>
            <span className={styles.detailsValue}>
              {product.units_available}
            </span>
          </div>
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Stock bodega</span>
            <span className={styles.detailsValue}>
              {product.warehouseStock}
            </span>
          </div>
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Stock zona franca</span>
            <span className={styles.detailsValue}>{product.freeZoneStock}</span>
          </div>
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Precio</span>
            <span className={styles.detailsValue}>
              {formatNumberToPrice(product.price)}
            </span>
          </div>
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Dimensiones</span>
            <span className={styles.detailsValue}>
              {product.widthCm}cm × {product.lengthCm}cm
            </span>
          </div>
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Área (m²)</span>
            <span className={styles.detailsValue}>{product.areaM2}</span>
          </div>
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Adhesivo</span>
            <span className={styles.detailsValue}>
              {product.adhesiveName || "-"}
            </span>
          </div>
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>
              Unidades adhesivo por lámina
            </span>
            <span className={styles.detailsValue}>
              {product.adhesiveUnitsPerSheet ?? "-"}
            </span>
          </div>
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Precio unidad adhesivo</span>
            <span className={styles.detailsValue}>
              {product.adhesiveUnitPrice
                ? formatNumberToPrice(product.adhesiveUnitPrice)
                : "-"}
            </span>
          </div>
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Sellante</span>
            <span className={styles.detailsValue}>
              {product.sealantName || "-"}
            </span>
          </div>
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>
              Cobertura sellante (m²/cuarto)
            </span>
            <span className={styles.detailsValue}>
              {product.sealantCoverageM2PerQuart ?? "-"}
            </span>
          </div>
          <div className={styles.detailsRow}>
            <span className={styles.detailsLabel}>Precio cuarto sellante</span>
            <span className={styles.detailsValue}>
              {product.sealantQuartPrice
                ? formatNumberToPrice(product.sealantQuartPrice)
                : "-"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
